import styled from "styled-components";
import {
  DimensaoDaConfig,
  declaracao,
  medidaDoBotaoDeAcesso,
  medidaDoLogo,
} from "../../../dominio/dimensaoDaConfig";
import { TomDaOrientacao } from "../../../dominio/orientacaoDaHome";

export const PageContainer = styled.div<{ backgroundColor: string }>`
  background-color: ${(props) => props.backgroundColor};
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

/**
 * O tamanho vem da configuração do PDV, em duas formas (número puro ou
 * `"Npx"`). Quem interpreta as duas é `dominio/dimensaoDaConfig.ts`: número
 * continua virando porcentagem, e a string em px — que antes produzia
 * `width: NaN%` e era descartada — passa a valer o número na escala fluida.
 */
export const AlfredoIcon = styled.img<{
  larguraDoLogo: DimensaoDaConfig;
  alturaDoLogo: DimensaoDaConfig;
}>`
  ${(props) => declaracao("width", medidaDoLogo(props.larguraDoLogo, 4))}
  ${(props) => declaracao("height", medidaDoLogo(props.alturaDoLogo, 4))}
  object-fit: contain; /* Mantém o aspecto do SVG, mostrando todo o conteúdo sem distorção */
  overflow: hidden; /* Garante que o conteúdo respeite o border-radius */
  &:hover {
    ${(props) => declaracao("width", medidaDoLogo(props.larguraDoLogo, 3))}
    ${(props) => declaracao("height", medidaDoLogo(props.alturaDoLogo, 3))}
  }
  transition: all 0.2s ease-in-out;
  cursor: pointer;
`;

export const ChooseOption = styled.div<{ textColor: string }>`
  color: ${(props) => props.textColor};
  font-weight: bold;
  font-size: var(--fs-2xl);
  margin-bottom: var(--sp-20);
`;

/**
 * O cartão de orientação — o que a Home diz quando falta um passo.
 *
 * A cor de fundo, a de texto e a dos botões da Home vêm da configuração do PDV
 * e podem ser qualquer coisa (o padrão é azul claro com letra branca; outra
 * loja pode ter fundo escuro). Por isso o cartão NÃO usa as cores do registro:
 * ele traz superfície branca quase opaca e letra escura, que se lê em cima de
 * qualquer fundo. A tarja da esquerda é o único sinal de tom.
 *
 * Só aparece nos estados em que a tela estava muda — no estado pronto
 * `OrientacaoDaHome` devolve `null` e nada disso é renderizado
 * (`dominio/orientacaoDaHome.ts`).
 */
const COR_DA_TARJA: Record<TomDaOrientacao, string> = {
  instrucao: "#1D50FF", // o azul do próprio Alfredo
  atencao: "#8F5E07", // âmbar de "falta um passo", nunca vermelho de erro
};

export const OrientacaoCartao = styled.div<{ tom: TomDaOrientacao }>`
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  max-width: calc(var(--u) * 620);
  margin-bottom: var(--sp-20);
  padding: var(--sp-16) var(--sp-20);
  border-radius: 12px;
  border-left: calc(var(--u) * 5) solid ${(props) => COR_DA_TARJA[props.tom]};
  background-color: rgba(255, 255, 255, 0.94);
  box-shadow: 0 1px 3px rgba(18, 32, 26, 0.16);
  text-align: left;
`;

export const OrientacaoTitulo = styled.strong<{ tom: TomDaOrientacao }>`
  font-size: var(--fs-lg);
  font-weight: 700;
  color: ${(props) => COR_DA_TARJA[props.tom]};
`;

export const OrientacaoTexto = styled.span`
  font-size: var(--fs-md);
  line-height: 1.45;
  color: #16211c;
`;

export const AccessButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--sp-20);
`;

/**
 * `larguraDoBotao`/`alturaDoBotao` em vez de `width`/`height`: o
 * styled-components 6 encaminha para o DOM toda prop que o `is-prop-valid`
 * reconhece como atributo HTML, e `width`/`height` são dois deles — o `<button>`
 * saía com `width="420px"` no HTML. Com o valor virando objeto isso viraria
 * `width="[object Object]"`. Os nomes em português não são atributos e ficam
 * retidos no styled-components. Sem efeito de pixel.
 */
export const AccessButton = styled.button<{
  title?: string;
  buttonsColor: string;
  textColor: string;
  larguraDoBotao: DimensaoDaConfig;
  alturaDoBotao: DimensaoDaConfig;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  border-radius: 30%;
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.buttonsColor};
  ${(props) => declaracao("width", medidaDoBotaoDeAcesso(props.larguraDoBotao))}
  ${(props) => declaracao("height", medidaDoBotaoDeAcesso(props.alturaDoBotao))}
  position: relative;
  &:hover {
    box-shadow: 0px 0px 50px white;
  }
  transition: box-shadow 0.5s ease-in-out;
  cursor: pointer;
  &::after {
    content: "${(props) => props.title}";
    width: 100%;
    font-size: var(--fs-2xl);
    font-weight: bold;
    position: absolute;
    bottom: calc(var(--u) * 20);
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const SaleOptionImg = styled.img`
  align-self: center;
  width: calc(var(--u) * 400);
  height: calc(var(--u) * 400);
`;

export const LogButton = styled.button<{
  textColor: string;
  buttonsColor: string;
}>`
  text-decoration: none;
  margin-top: var(--sp-20);
  padding: var(--sp-10) var(--sp-20);
  border-radius: 50px;
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.buttonsColor};
  font-weight: bold;
  font-size: var(--fs-2xl);
  cursor: pointer;
`;
